/**
 * Saídas que cada tipo de nó oferece. `failed` é opcional: desconectada, a
 * falha é registrada e o fluxo segue pela `next` (mesmo comportamento das
 * automações, que continuavam nas ações seguintes).
 */
export const SALES_FLOW_NODE_HANDLES = {
    START: ['next'],
    SEND_WHATSAPP: ['next', 'failed'],
    SEND_TEMPLATE: ['next', 'failed'],
    SEND_EMAIL: ['next', 'failed'],
    NOTIFY_USER: ['next'],
    PAUSE: ['replied', 'timeout'],
    CONDITION: ['true', 'false'],
    MOVE_STAGE: ['next', 'failed'],
    ASSIGN_USER: ['next', 'failed'],
    ASSIGN_WALLET: ['next', 'failed'],
    SET_FIELD: ['next', 'failed'],
    TAG: ['next', 'failed'],
    SET_TEMPERATURE: ['next', 'failed'],
    SET_LIFECYCLE: ['next', 'failed'],
    CREATE_DEAL: ['next', 'failed'],
    ADD_NOTE: ['next'],
    CREATE_TASK: ['next', 'failed'],
    WEBHOOK: ['next', 'failed'],
    GO_TO: [],
    START_FLOW: ['next', 'failed'],
    STOP: [],
};
/** Tipos de gatilho que o motor já executa. Os demais entram na fase 4. */
export const SALES_FLOW_SUPPORTED_TRIGGERS = [
    'EVENT',
    'STAGE_ENTERED',
    'TIME_IN_STAGE',
    'DATE_FIELD_OFFSET',
    'NO_REPLY',
    'INBOUND_AFTER_SILENCE',
    'OUTBOUND_READ',
    'SCHEDULE_DAILY',
    'CONVERSATION_CLOSED',
    'MANUAL',
];
/** Gatilhos de tempo: não vêm de evento, a varredura periódica os encontra. */
export const SALES_FLOW_TIME_TRIGGERS = ['TIME_IN_STAGE', 'DATE_FIELD_OFFSET', 'NO_REPLY', 'SCHEDULE_DAILY'];
/** Limite de nós executados numa retomada, contra laço de GO_TO. */
export const SALES_FLOW_MAX_STEPS_PER_RESUME = 50;
/** Profundidade máxima de fluxos disparando fluxos. */
export const SALES_FLOW_MAX_DEPTH = 3;
const SEND_TYPES = ['SEND_WHATSAPP', 'SEND_TEMPLATE', 'SEND_EMAIL'];
const filled = (value) => value !== undefined && value !== null && String(value).trim() !== '';
/** Campos sem os quais o passo não faz nada. Mensagem legível para a tela. */
const REQUIRED_CONFIG = {
    SEND_WHATSAPP: [{ field: 'content', message: 'Escreva a mensagem.' }],
    SEND_TEMPLATE: [{ field: 'templateId', message: 'Escolha o template.' }],
    SEND_EMAIL: [
        { field: 'subject', message: 'Defina o assunto do e-mail.' },
        { field: 'content', message: 'Escreva o conteúdo do e-mail.' },
    ],
    NOTIFY_USER: [{ field: 'message', message: 'Escreva a mensagem da notificação.' }],
    ASSIGN_WALLET: [{ field: 'walletId', message: 'Escolha a carteira.' }],
    TAG: [{ field: 'tag', message: 'Defina a tag.' }],
    SET_FIELD: [{ field: 'field', message: 'Escolha o campo.' }],
    SET_TEMPERATURE: [{ field: 'temperature', message: 'Escolha a temperatura.' }],
    SET_LIFECYCLE: [{ field: 'lifecycleStatus', message: 'Escolha o ciclo de vida.' }],
    CREATE_DEAL: [
        { field: 'pipelineId', message: 'Escolha o funil.' },
        { field: 'stageId', message: 'Escolha a etapa.' },
    ],
    ADD_NOTE: [{ field: 'content', message: 'Escreva a nota.' }],
    CREATE_TASK: [{ field: 'title', message: 'Descreva a tarefa.' }],
    WEBHOOK: [{ field: 'url', message: 'Informe a URL.' }],
    START_FLOW: [{ field: 'flowId', message: 'Escolha o fluxo a iniciar.' }],
};
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Telefone avulso: só dígitos contam; DDD + número no mínimo. */
export function isValidRecipientPhone(value) {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
}
export function isValidRecipientEmail(value) {
    return EMAIL_PATTERN.test(value.trim());
}
function validateRecipient(type, recipient) {
    if (!recipient || recipient.kind === 'LEAD' || recipient.kind === 'ASSIGNED_USER')
        return [];
    if (recipient.kind === 'USERS') {
        return (recipient.userIds ?? []).length ? [] : ['Escolha pelo menos um usuário para receber.'];
    }
    const contacts = (recipient.contacts ?? []).map(c => c.trim()).filter(Boolean);
    if (!contacts.length) {
        return [type === 'SEND_EMAIL' ? 'Informe pelo menos um e-mail.' : 'Informe pelo menos um número.'];
    }
    const invalid = contacts.filter(c => (type === 'SEND_EMAIL' ? !isValidRecipientEmail(c) : !isValidRecipientPhone(c)));
    if (!invalid.length)
        return [];
    return [type === 'SEND_EMAIL' ? `E-mail inválido: ${invalid.join(', ')}.` : `Número inválido: ${invalid.join(', ')}.`];
}
/**
 * Validação do grafo antes de publicar. Roda no editor (aviso imediato) e no
 * backend (recusa a publicação com erro). Pura: sem banco.
 */
export function validateSalesFlowGraph(graph) {
    const issues = [];
    const byKey = new Map(graph.nodes.map(n => [n.key, n]));
    if (graph.nodes.length !== byKey.size) {
        issues.push({ severity: 'error', message: 'Há passos com a mesma chave.' });
    }
    const starts = graph.nodes.filter(n => n.type === 'START');
    if (starts.length !== 1) {
        issues.push({ severity: 'error', message: 'O fluxo precisa de exatamente um início.' });
    }
    if (graph.triggers.length === 0) {
        issues.push({ severity: 'error', message: 'O fluxo precisa de pelo menos um gatilho.' });
    }
    for (const trigger of graph.triggers) {
        if (!SALES_FLOW_SUPPORTED_TRIGGERS.includes(trigger.type)) {
            issues.push({ severity: 'error', message: `Gatilho ${trigger.type} ainda não está disponível.` });
        }
        if (trigger.type === 'EVENT' && !trigger.event) {
            issues.push({ severity: 'error', message: 'Gatilho de evento sem o evento escolhido.' });
        }
        if (trigger.type === 'STAGE_ENTERED' && !trigger.stageId) {
            issues.push({ severity: 'error', message: 'Gatilho "entrou na etapa" sem a etapa escolhida.' });
        }
        const config = (trigger.config ?? {});
        if (trigger.type === 'TIME_IN_STAGE') {
            if (!trigger.stageId)
                issues.push({ severity: 'error', message: 'Gatilho "parado na etapa" sem a etapa escolhida.' });
            if (!(Number(config.afterMinutes) > 0))
                issues.push({ severity: 'error', message: 'Gatilho "parado na etapa" precisa de um tempo maior que zero.' });
        }
        if (trigger.type === 'DATE_FIELD_OFFSET') {
            if (!config.field)
                issues.push({ severity: 'error', message: 'Gatilho de data sem a data escolhida.' });
            if (!Number.isFinite(Number(config.offsetMinutes)))
                issues.push({ severity: 'error', message: 'Gatilho de data sem o tempo antes ou depois.' });
        }
        if (trigger.type === 'NO_REPLY' && !(Number(config.afterMinutes) > 0)) {
            issues.push({ severity: 'error', message: 'Gatilho "sem resposta" precisa de um tempo maior que zero.' });
        }
        if (trigger.type === 'INBOUND_AFTER_SILENCE' && !(Number(config.silenceMinutes) > 0)) {
            issues.push({ severity: 'error', message: 'Gatilho "mensagem após silêncio" precisa de um tempo maior que zero.' });
        }
        if (trigger.type === 'SCHEDULE_DAILY') {
            if (!trigger.stageId)
                issues.push({ severity: 'error', message: 'Gatilho diário sem a etapa: ele vale para as oportunidades abertas nela.' });
            if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(config.time ?? '')))
                issues.push({ severity: 'error', message: 'Gatilho diário sem um horário válido.' });
        }
        if ((trigger.delayMinutes ?? 0) < 0) {
            issues.push({ severity: 'error', message: 'O atraso do gatilho não pode ser negativo.' });
        }
    }
    const seenHandles = new Set();
    for (const edge of graph.edges) {
        const from = byKey.get(edge.fromKey);
        if (!from) {
            issues.push({ severity: 'error', message: `Ligação saindo de um passo inexistente (${edge.fromKey}).` });
            continue;
        }
        if (!byKey.has(edge.toKey)) {
            issues.push({ nodeKey: from.key, severity: 'error', message: 'Ligação para um passo inexistente.' });
        }
        if (!SALES_FLOW_NODE_HANDLES[from.type].includes(edge.handle)) {
            issues.push({ nodeKey: from.key, severity: 'error', message: `Saída "${edge.handle}" não existe neste passo.` });
        }
        const handleId = `${edge.fromKey}:${edge.handle}`;
        if (seenHandles.has(handleId)) {
            issues.push({ nodeKey: from.key, severity: 'error', message: `A saída "${edge.handle}" está ligada a mais de um passo.` });
        }
        seenHandles.add(handleId);
    }
    for (const node of graph.nodes) {
        const config = (node.config ?? {});
        for (const required of REQUIRED_CONFIG[node.type] ?? []) {
            if (!filled(config[required.field])) {
                issues.push({ nodeKey: node.key, severity: 'error', message: required.message });
            }
        }
        if (node.type === 'MOVE_STAGE' && !filled(config.stageName) && !filled(config.stageId)) {
            issues.push({ nodeKey: node.key, severity: 'error', message: 'Escolha a etapa de destino.' });
        }
        if (node.type === 'ASSIGN_USER') {
            const roundRobin = config.mode === 'ROUND_ROBIN';
            if (roundRobin && (config.userIds ?? []).length < 2) {
                issues.push({ nodeKey: node.key, severity: 'error', message: 'O rodízio precisa de pelo menos dois usuários.' });
            }
            if (!roundRobin && !filled(config.userId)) {
                issues.push({ nodeKey: node.key, severity: 'error', message: 'Escolha o responsável.' });
            }
        }
        if (node.type === 'PAUSE' && !(Number(config.timeoutMinutes) > 0)) {
            issues.push({ nodeKey: node.key, severity: 'error', message: 'A pausa precisa de um tempo maior que zero.' });
        }
        if (node.type === 'GO_TO' && !byKey.has(config.targetKey)) {
            issues.push({ nodeKey: node.key, severity: 'error', message: '"Ir para" sem um passo de destino válido.' });
        }
        if (node.type === 'CREATE_TASK' && config.dueInMinutes != null && !(Number(config.dueInMinutes) >= 0)) {
            issues.push({ nodeKey: node.key, severity: 'error', message: 'O prazo da tarefa não pode ser negativo.' });
        }
        if (SEND_TYPES.includes(node.type)) {
            for (const message of validateRecipient(node.type, config.recipient)) {
                issues.push({ nodeKey: node.key, severity: 'error', message });
            }
        }
        if (SEND_TYPES.includes(node.type) && !seenHandles.has(`${node.key}:failed`)) {
            issues.push({ nodeKey: node.key, severity: 'warning', message: 'Se o envio falhar, o fluxo segue como se tivesse enviado.' });
        }
    }
    // Alcançáveis a partir do início (GO_TO conta como ligação).
    if (starts.length === 1) {
        const reachable = new Set();
        const queue = [starts[0].key];
        while (queue.length) {
            const key = queue.shift();
            if (reachable.has(key))
                continue;
            reachable.add(key);
            for (const edge of graph.edges)
                if (edge.fromKey === key)
                    queue.push(edge.toKey);
            const node = byKey.get(key);
            const target = node?.type === 'GO_TO' ? node.config?.targetKey : null;
            if (target)
                queue.push(target);
        }
        for (const node of graph.nodes) {
            if (!reachable.has(node.key)) {
                issues.push({ nodeKey: node.key, severity: 'warning', message: 'Passo solto: nenhum caminho chega até ele.' });
            }
        }
    }
    return issues;
}
//# sourceMappingURL=graph.js.map